// src/scripts/userContext.js

import {
  doc,
  getDoc,
} from "firebase/firestore";

import {
  auth,
  db,
} from "@/firebase";

import {
  toISODate,
  parseISODate,
  addDaysISO,
} from "@/scripts/sharedHelpers/formatter";

function s(v) {
  return String(
    v ?? ""
  ).trim();
}

function wait(
  ms
) {
  return new Promise(
    (
      resolve
    ) => {
      setTimeout(
        resolve,
        ms
      );
    }
  );
}

function isPermissionDenied(
  error
) {
  const code =
    s(
      error?.code
    ).toLowerCase();

  const message =
    s(
      error?.message
    ).toLowerCase();

  return (
    code ===
      "permission-denied" ||
    code ===
      "firestore/permission-denied" ||
    message.includes(
      "missing or insufficient permissions"
    )
  );
}

/**
 * Read the authenticated user's Firestore profile.
 *
 * Normal operation:
 *
 *   users/{current Firebase UID}
 *
 * If the very first read immediately after authentication
 * receives permission-denied, refresh the Firebase ID token
 * and retry twice.
 *
 * This does NOT bypass Firestore rules.
 * A genuine permission problem still fails.
 */
export async function readAuthenticatedUserProfile(
  uid
) {
  const cleanUid =
    s(
      uid
    );

  if (!cleanUid) {
    throw new Error(
      "Cannot load user profile without a Firebase UID."
    );
  }

  const userRef =
    doc(
      db,
      "users",
      cleanUid
    );

  /*
   * If the authenticated user is already available and
   * matches the requested profile, make sure Firebase has
   * generated an ID token before the first Firestore call.
   */
  const initialUser =
    auth.currentUser;

  if (
    initialUser?.uid ===
    cleanUid
  ) {
    await initialUser.getIdToken();
  }

  const retryDelays = [
    0,
    200,
    500,
  ];

  let lastError =
    null;

  for (
    let attempt = 0;
    attempt <
    retryDelays.length;
    attempt += 1
  ) {
    /*
     * Attempts 2 and 3 only happen after an actual
     * permission-denied from Firestore.
     */
    if (
      attempt >
      0
    ) {
      const currentUser =
        auth.currentUser;

      /*
       * Never try to work around a genuine UID mismatch.
       *
       * If this happens, the console will now tell us
       * exactly what the Firebase Authentication UID was.
       */
      if (
        !currentUser ||
        currentUser.uid !==
          cleanUid
      ) {
        console.error(
          "[USER CONTEXT] UID mismatch during profile read",
          {
            requestedUid:
              cleanUid,

            authenticatedUid:
              currentUser
                ?.uid ||
              "",

            authenticatedEmail:
              currentUser
                ?.email ||
              "",
          }
        );

        throw lastError ||
          new Error(
            "The authenticated Firebase UID does not match the CoreDeck user profile UID."
          );
      }

      /*
       * Force-refresh the ID token.
       *
       * This changes no roles, claims, rules or database data.
       */
      await currentUser.getIdToken(
        true
      );

      await wait(
        retryDelays[
          attempt
        ]
      );
    }

    try {
      return await getDoc(
        userRef
      );
    } catch (error) {
      lastError =
        error;

      const permissionDenied =
        isPermissionDenied(
          error
        );

      if (
        !permissionDenied ||
        attempt ===
          retryDelays.length -
            1
      ) {
        console.error(
          "[USER CONTEXT] Firestore profile read failed",
          {
            requestedUid:
              cleanUid,

            authenticatedUid:
              auth.currentUser
                ?.uid ||
              "",

            authenticatedEmail:
              auth.currentUser
                ?.email ||
              "",

            code:
              error?.code ||
              "",

            message:
              error?.message ||
              "",

            attempt:
              attempt +
              1,

            raw:
              error,
          }
        );

        throw error;
      }

      console.warn(
        "[USER CONTEXT] Profile read permission denied; refreshing Firebase token before retry",
        {
          requestedUid:
            cleanUid,

          authenticatedUid:
            auth.currentUser
              ?.uid ||
            "",

          attempt:
            attempt +
            1,
        }
      );
    }
  }

  throw lastError ||
    new Error(
      "CoreDeck could not load the user profile."
    );
}

/**
 * Calculate Sunday week-ending from YYYY-MM-DD.
 */
function calcWeekEnding(
  tradingIso
) {
  const d =
    parseISODate(
      tradingIso
    );

  if (!d) {
    return "";
  }

  const dow =
    d.getDay();

  const diff =
    (7 - dow) %
    7;

  return addDaysISO(
    tradingIso,
    diff
  );
}

/**
 * Store CoreDeck context after successful authentication.
 */
export async function storeUserContext(
  uid
) {
  const cleanUid =
    s(
      uid
    );

  if (!cleanUid) {
    throw new Error(
      "Cannot load user context without a Firebase UID."
    );
  }

  const userSnap =
    await readAuthenticatedUserProfile(
      cleanUid
    );

  const data =
    userSnap.exists()
      ? userSnap.data()
      : {};

  /*
   * Existing venue selection behaviour intentionally
   * retained unchanged.
   */
  const venue =
    s(
      data.activeVenueId
    ) ||
    s(
      data.venue_id
    ) ||
    s(
      data.venueId
    ) ||
    s(
      data.venue
    ) ||
    "";

  if (
    venue
  ) {
    localStorage.setItem(
      "coredeck_venue",
      venue
    );

    localStorage.setItem(
      "venue",
      venue
    );
  } else {
    localStorage.removeItem(
      "coredeck_venue"
    );

    localStorage.removeItem(
      "venue"
    );
  }

  const d =
    new Date();

  d.setHours(
    12,
    0,
    0,
    0
  );

  const todayIso =
    toISODate(
      d
    );

  const weekEndingIso =
    calcWeekEnding(
      todayIso
    );

  localStorage.setItem(
    "coredeck_trading_date",
    todayIso
  );

  localStorage.setItem(
    "coredeck_week_ending",
    weekEndingIso
  );

  // Legacy compatibility
  localStorage.setItem(
    "tradingDate",
    todayIso
  );

  localStorage.setItem(
    "businessDate",
    todayIso
  );

  localStorage.setItem(
    "weekEnding",
    weekEndingIso
  );

  return {
    venue,

    tradingDate:
      todayIso,

    weekEnding:
      weekEndingIso,
  };
}